<script>
import {
  RECURRING_FREQUENCY_UNIT_VALUES as recurringFrequencyUnitValues,
  RECURRING_DAYS as recurringDays,
  RECURRING_DAYS_JS,
  RECURRING_PREFIXES,
} from '@events/constant'
import format from 'date-fns/format'
import getWeekOfMonth from 'date-fns/getWeekOfMonth'

export default {
  props: {
    recurring: {
      type: Object,
      required: true,
    },
    startDate: {
      type: Date,
      required: true,
    },
  },
  data: () => ({
    recurringForm: {},
    weekDays: {},
    monthDaysSelected: '',
    recurringFrequencyUnitValues,
    recurringDays,
  }),
  computed: {
    isRecurringOn() {
      return ['w', 'm'].includes(this.recurringForm.recurringFrequencyUnit)
    },
    recurringOn() {
      if (!this.isRecurringOn) return

      switch (this.recurringForm.recurringFrequencyUnit) {
        case 'w':
          return Object.entries(this.weekDays)
            .filter(([, selected]) => selected)
            .map(([day]) => day)
            .join(',')

        case 'm':
          return this.monthDaysSelected === 'day_of_month'
            ? format(this.startDate, 'd')
            : `${getWeekOfMonth(this.startDate)}-${RECURRING_DAYS_JS.indexOf(
                format(this.startDate, 'EEEE')
              )}`

        default:
          return ''
      }
    },
    weekOfMonth() {
      return RECURRING_PREFIXES[getWeekOfMonth(this.startDate) - 1]
    },
    monthDays() {
      return [
        {
          value: 'day_of_month',
          name: `Monthly on day ${format(this.startDate, 'd')}`,
        },
        {
          value: 'day_of_week_in_month',
          name: `Monthly on the ${
            RECURRING_PREFIXES[getWeekOfMonth(this.startDate) - 1]
          } ${format(this.startDate, 'EEEE')}`,
        },
      ]
    },
  },
  created() {
    const {
      recurringFrequencyUnit,
      recurringOn,
    } = (this.recurringForm = this.recurring)

    if (recurringFrequencyUnit === 'w') {
      const weekDays = {}
      recurringOn.split(',').map((index) => (weekDays[index] = true))
      this.weekDays = { ...weekDays }
    }

    if (recurringFrequencyUnit === 'm') {
      if (recurringOn.split('-').length === 1) {
        this.monthDaysSelected = 'day_of_month'
      } else {
        this.monthDaysSelected = 'day_of_week_in_month'
      }
    }
  },
  methods: {
    onCloseClick() {
      this.$emit('close')
    },
    onSaveClick() {
      const recurringForm = this.isRecurringOn
        ? {
            ...this.recurringForm,
            recurringOn: this.recurringOn,
          }
        : this.recurringForm

      this.$emit('save', recurringForm)
      this.$emit('close')
    },
  },
}
</script>

<template>
  <BaseModalWindow
    modalcls="w-full max-w-1.95xl bg-white rounded-xl mx-auto px-10 py-8 shadow-box relative left-0"
    :visible="true"
    @close="onCloseClick"
  >
    <template v-slot:body>
      <h3 class="block pb-6 italic font-brandon text-blue-darker text-35xl">
        RECURRING FORM
      </h3>
      <div class="space-y-4">
        <div class="grid items-center grid-cols-3 gap-4">
          <label for="recurringFrequency">Repeat every</label>
          <BaseInputText
            id="recurringFrequency"
            v-model="recurringForm.recurringFrequency"
            min="0"
            type="number"
            value="1"
          />

          <BaseSelectBox
            v-model="recurringForm.recurringFrequencyUnit"
            :options="recurringFrequencyUnitValues"
          />
        </div>

        <div
          v-if="['w', 'm'].includes(recurringForm.recurringFrequencyUnit)"
          class="grid items-center grid-cols-3 gap-4"
        >
          <label for="recurringFrequency">Repeat on</label>

          <template v-if="recurringForm.recurringFrequencyUnit === 'w'">
            <ul class="flex col-span-2 gap-2">
              <li v-for="(day, index) in recurringDays" :key="day">
                <input
                  :id="`weekday-${day}`"
                  v-model="weekDays[index]"
                  type="checkbox"
                  class="hidden"
                />
                <label
                  class="inline-flex items-center justify-center w-8 h-8 border rounded-full cursor-pointer text-sml border-blue-default"
                  :for="`weekday-${day}`"
                  :class="{ 'bg-blue text-white': weekDays[index] }"
                >
                  {{ day.charAt(0) }}
                </label>
              </li>
            </ul>
          </template>

          <template v-if="recurringForm.recurringFrequencyUnit === 'm'">
            <BaseSelectBox
              v-model="monthDaysSelected"
              class="col-span-2"
              :options="monthDays"
            />
          </template>
        </div>

        <div>
          Ends
        </div>

        <div class="grid items-center grid-cols-3 gap-4">
          <label class="inline-flex items-center h-10 cursor-pointer">
            <input
              v-model="recurringForm.recurringEndType"
              name="recurring_end_type"
              type="radio"
              class="mr-3"
              value="never"
            />
            <span>Never</span>
          </label>
        </div>

        <div class="grid items-center grid-cols-3 gap-4">
          <label class="inline-flex items-center cursor-pointer">
            <input
              v-model="recurringForm.recurringEndType"
              name="recurring_end_type"
              type="radio"
              class="mr-3"
              value="date"
            />
            <span>On</span>
          </label>

          <div
            :class="{ 'opacity-50': recurringForm.recurringEndType !== 'date' }"
          >
            <BaseInputText
              id="recurringFrequency"
              v-model="recurringForm.recurringEndValueDate"
              type="date"
              @click="recurringForm.recurringEndType = 'date'"
            />
          </div>
        </div>

        <div class="grid items-center grid-cols-3 gap-4">
          <label class="inline-flex items-center cursor-pointer">
            <input
              v-model="recurringForm.recurringEndType"
              name="recurring_end_type"
              type="radio"
              class="mr-3"
              value="limit"
            />
            <span>After</span>
          </label>

          <div
            class="grid items-center grid-cols-2 col-span-2 gap-4"
            :class="{
              'opacity-50': recurringForm.recurringEndType !== 'limit',
            }"
          >
            <BaseInputText
              id="recurringFrequency"
              v-model="recurringForm.recurringEndValueLimit"
              type="number"
              @click="recurringForm.recurringEndType = 'limit'"
            />
            <span>occurences</span>
          </div>
        </div>

        <div>
          <button
            class="button primary-button h-7.5 leading-relax text-sml rounded-small-button px-3 mt-3"
            @click="onSaveClick"
          >
            Save
          </button>
          <button
            class="button secondary-button h-7.5 leading-relax text-sml rounded-small-button px-3 mt-3"
            @click="onCloseClick"
          >
            Cancel
          </button>
        </div>
      </div>
    </template>
  </BaseModalWindow>
</template>
